import { z } from 'zod';

/**
 * Catalog liveness canary.
 *
 * The referral money path is the only catalog vocabulary the Koa API emits, so
 * "zero `referral.*` events in PostHog" has two indistinguishable readings: no
 * referral traffic (true on 2026-08-20 — not one `?ref=` pageview had ever
 * happened), or `initObservability()` never installed the publisher and every
 * `safeEmit` validated its payload and dropped it silently. The affiliate
 * launch is the first real traffic this path will ever see; discovering the
 * second reading *then* is the ten-day-silent-referral bug all over again, one
 * level up.
 *
 * One event per process boot, emitted through the full catalog → publisher →
 * capture path by the wiring itself, separates the readings permanently:
 * canary present + no referrals = quiet program; canary absent = dead pipe.
 * Deploys and restarts are the natural cadence — a handful of events a week,
 * each one proof the pipe was alive when the process came up.
 */
export const CANARY_SERVICES = [
  /** The Koa API — publishes to PostHog project 10465 "OnArk - App". */
  'api',
  /** The worker — publishes to project 10583 "OnArk - Automations". */
  'worker',
  /**
   * The Next.js server runtime — tRPC procedures and Server Components, which
   * publish to 10465 "OnArk - App" alongside the Koa API.
   *
   * This runtime is the reason the canary earns its keep a second time. Its
   * catalog had NO publisher at all: `utils/observability.ts` wires one, but
   * that module is imported only by `'use client'` components, and the
   * `capture` it wires returns early on `typeof window === 'undefined'`. So
   * every `catalog.emit` in a tRPC router validated its payload and fanned out
   * to nobody — `admin.affiliate_approved`, `resources.template_copied` and
   * the rest were silently dropped from the day they shipped.
   *
   * Registered once, from instrumentation.ts, on a process-wide registry
   * (see `registerCatalogPublishers`), so every compiled copy of the catalog
   * in the process publishes through it.
   */
  'frontend',
] as const;

export type CanaryService = (typeof CANARY_SERVICES)[number];

export const canarySchemas = {
  /**
   * The catalog's PostHog publisher was installed in a server process. Emitted
   * by `createPosthogCatalogWiring`'s returned init, so it exercises the exact
   * pipeline every other catalog event rides — an event that arrives proves
   * validation, identity resolution, and the app's `capture` transport all ran.
   */
  'observability.catalog_wired': z.object({
    service: z.enum(CANARY_SERVICES),
  }),
};
